<template>
  <div class="volumeIndicator">
    <div class="soundPreview">
      <div class="greyBackground" />
      <div class="bars">
        <div v-for="bar in bars" :key="bar" class="bar" />
      </div>
      <div :style="{ width: `${(volume / 3) * 100}%` }" class="soundBar" />
    </div>

    <div
      v-if="deviceKind === 'audioinput'"
      :class="['recordingIndicator', { recording: !replayingActive }]"
    >
      <FontAwesomeIcon class="circle indicator" :icon="faCircle" />
      <span>{{ replayingActive ? 'Replaying' : 'Recording' }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import type { Ref } from 'vue'
import type {
  DeviceKind,
  AudioAnalyserVolumeCallbackData,
  AudioPlayer,
  ManagedTimeout
} from '@/customTypes'
import { storeToRefs } from 'pinia'

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCircle } from '@fortawesome/free-solid-svg-icons'

// Stores
import { useMediaDevicesStore } from '@/stores/MediaDevicesStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useAudioAnalyserStore } from '@/stores/AudioAnalyserStore'

// Helpers
import { useTimeout } from '@/utils/helpers'

// Services
import Logger from '@/services/Logger.js'

const logger: any = new Logger('AudioSettingsAnalyser', true)

const props = defineProps({
  deviceKind: {
    type: String as () => DeviceKind,
    required: true
  },
  dropdownFocused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  (e: 'sound-picked'): void
  (e: 'playback-finished'): void
  (e: 'output-sound'): void
  (e: 'output-silence'): void
}>()

const numOfBars = ref(8)
const volume = ref(0)
const volumeThreshold: number = 0.3
const playbackWaitTime: number = 1000
const recordingLengthLimit: number = 3000

const REFRESH_PROMPT_DELAY = 5000
const playbackFinishedCallback: Ref<Function> = ref(onPlaybackFinished)

const mediaDevicesStore = useMediaDevicesStore()
const { recordingStream, selectedAudioInputDeviceId, selectedAudioOutputDeviceId } =
  storeToRefs(mediaDevicesStore)

const soundEffectsStore = useSoundEffectsStore()
const { audioContext } = storeToRefs(soundEffectsStore)

const audioAnalyserStore = useAudioAnalyserStore()
const { outputPlaybackActive } = storeToRefs(audioAnalyserStore)
const { setManagedTimeout, clearManagedTimeout } = useTimeout()

const playbackTimeoutId = ref<ManagedTimeout | null>(null)
const playbackSource: Ref<AudioBufferSourceNode | null> = ref(null)
const replayingActive: Ref<boolean> = ref(false)
const refreshPromptTimeoutId = ref<ManagedTimeout | null>(null)
const hasAutoRefreshed = ref(false)
const refreshingInput = ref(false)

const lastLoggedVolume = ref<number | null>(null)
const isOutputCurrentlySilent = ref(true)
const playbackStartedAt = ref<number | null>(null)
const playbackEndedAt = ref<number | null>(null)
const ZERO_EDGE_GRACE_MS = 200 // Allow brief zero readings when playback starts or ends

const audioPlayer = ref<AudioPlayer | null>(null)
let isUnmounting = false
let analyserSetupRequestId = 0

const bars = computed(() => {
  return Array.from({ length: numOfBars.value }, (_, i) => i)
})

function clearAutoRefreshTimeout() {
  clearManagedTimeout(refreshPromptTimeoutId)
  refreshPromptTimeoutId.value = null
}

function scheduleAutoRefresh() {
  if (props.deviceKind !== 'audioinput') return
  if (hasAutoRefreshed.value || refreshPromptTimeoutId.value || refreshingInput.value) return

  refreshPromptTimeoutId.value = setManagedTimeout(async () => {
    if (hasAutoRefreshed.value || refreshingInput.value) return

    const deviceId = selectedAudioInputDeviceId.value || ''
    refreshingInput.value = true
    refreshPromptTimeoutId.value = null
    hasAutoRefreshed.value = true

    try {
      await mediaDevicesStore.changeInputDevice(deviceId, true)
      logger.info('Auto-refreshed microphone device due to no sound detected')
    } catch (error) {
      logger.error('Failed to auto-refresh microphone selection', error)
    } finally {
      refreshingInput.value = false
      refreshPromptTimeoutId.value = null
    }
  }, REFRESH_PROMPT_DELAY)
}

async function triggerAutoRefresh() {
  if (props.deviceKind !== 'audioinput') return
  if (hasAutoRefreshed.value || refreshingInput.value) {
    logger.info('Auto-refresh already attempted or in progress, skipping')
    return
  }

  clearAutoRefreshTimeout()

  await nextTick()

  const deviceId = selectedAudioInputDeviceId.value || ''
  refreshingInput.value = true
  hasAutoRefreshed.value = true

  try {
    await mediaDevicesStore.changeInputDevice(deviceId, true)
    logger.info('Triggered auto-refresh microphone device after no sound detected notice')
  } catch (error) {
    logger.error('Failed to trigger auto-refresh microphone selection', error)
  } finally {
    refreshingInput.value = false
  }
}

onMounted(() => {
  logger.info('AudioSettingsVolumeIndicator mounted', props.deviceKind)
  setupAudioAnalyser().catch((error) => {
    logger.error('Initial analyser setup failed', error)
  })
  scheduleAutoRefresh()
})

onBeforeUnmount(() => {
  logger.info('Unmounting AudioSettingsVolumeIndicator')
  isUnmounting = true
  clearManagedTimeout(playbackTimeoutId)
  clearManagedTimeout(refreshPromptTimeoutId)
  playbackFinishedCallback.value = () => {}
  stopPlayback()
  stopRecording() // Stop recording and remove callback
  audioAnalyserStore.destroyAudioAnalyser(recordingStream.value ?? undefined, props.deviceKind)

  logger.info('AudioSettingsVolumeIndicator is Unmounting')
  analyserSetupRequestId += 1
})

watch(
  () => props.dropdownFocused,
  (isFocused) => {
    if (isFocused) {
      logger.info('Dropdown focused, stopping recording and playback')
      stopRecording()
      stopPlayback()
      volume.value = 0
      clearAutoRefreshTimeout()
      playbackStartedAt.value = null
      playbackEndedAt.value = Date.now()
      isOutputCurrentlySilent.value = true
      audioAnalyserStore.destroyAudioAnalyser(recordingStream.value ?? undefined, props.deviceKind)
    } else {
      if (props.deviceKind === 'audiooutput') {
        logger.info('Dropdown blurred, refreshing audio output analyser')
        analyserSetupRequestId += 1
        audioAnalyserStore.destroyAudioAnalyser(recordingStream.value ?? undefined, 'audiooutput')
        setupAudioAnalyser().catch((error) => {
          logger.error('Failed to refresh audio output analyser after blur', error)
        })
      } else if (props.deviceKind === 'audioinput') {
        logger.info('Dropdown blurred, restarting audio input analyser')
        analyserSetupRequestId += 1
        setupAudioAnalyser()
          .catch((error) => {
            logger.error('Failed to restart audio input analyser after blur', error)
          })
          .finally(() => {
            scheduleAutoRefresh()
          })
      } else {
        scheduleAutoRefresh()
      }
    }
  }
)

watch(recordingStream, (newStream, oldStream) => {
  if (props.deviceKind === 'audioinput') {
    logger.info('Recording stream changed', { newStream, oldStream })
    playbackFinishedCallback.value = () => {}
    stopPlayback()
    stopRecording(onRecordingFinished)
    if (oldStream) {
      audioAnalyserStore.destroyAudioAnalyser(oldStream, props.deviceKind)
    }
    if (!newStream) {
      scheduleAutoRefresh()
      return
    }
    analyserSetupRequestId += 1
    setupAudioAnalyser().catch((error) => {
      logger.error('Failed to setup analyser after recording stream change', error)
    })
    playbackFinishedCallback.value = onPlaybackFinished
    scheduleAutoRefresh()
    return
  }

  if (props.deviceKind === 'audiooutput' && newStream && !oldStream) {
    logger.info('Recording stream available for audio output analyser', {
      newStream,
      oldStream
    })
    analyserSetupRequestId += 1
    setupAudioAnalyser().catch((error) => {
      logger.error('Failed to setup analyser after recording stream available', error)
    })
  }
})

watch(selectedAudioInputDeviceId, (newDeviceId, oldDeviceId) => {
  if (props.deviceKind !== 'audioinput') return
  if (newDeviceId !== oldDeviceId && oldDeviceId !== undefined) {
    logger.info('Audio input device manually changed, resetting auto-refresh flag')
    hasAutoRefreshed.value = false
    clearAutoRefreshTimeout()
  }
})

watch(selectedAudioOutputDeviceId, (newDeviceId, oldDeviceId) => {
  if (props.deviceKind !== 'audiooutput') return
  if (newDeviceId === oldDeviceId) return

  logger.info('Audio output device changed, refreshing analyser', { newDeviceId, oldDeviceId })
  analyserSetupRequestId += 1
  audioAnalyserStore.destroyAudioAnalyser(undefined, 'audiooutput')
  setupAudioAnalyser().catch((error) => {
    logger.error('Failed to setup analyser after output device change', error)
  })
})

watch(outputPlaybackActive, (isPlaying) => {
  if (props.deviceKind !== 'audiooutput') return
  logger.info('Test track playback state changed', { isPlaying })
  if (isPlaying) {
    const now = Date.now()
    playbackStartedAt.value = now
    playbackEndedAt.value = null
    if (isOutputCurrentlySilent.value) {
      isOutputCurrentlySilent.value = false
      emit('output-sound')
    }
  } else {
    const now = Date.now()
    playbackStartedAt.value = null
    playbackEndedAt.value = now
    volume.value = 0
    if (!isOutputCurrentlySilent.value) {
      isOutputCurrentlySilent.value = true
      emit('output-silence')
    }
  }
})

function volumeChangeCallback(data: AudioAnalyserVolumeCallbackData) {
  if (props.dropdownFocused) {
    volume.value = 0 // Reset volume to prevent stale display
    if (!isOutputCurrentlySilent.value && props.deviceKind === 'audiooutput') {
      isOutputCurrentlySilent.value = true
      emit('output-silence')
    }
    return
  }

  const roundedVolume = Math.round(data.volume * 100) / 100
  if (roundedVolume !== lastLoggedVolume.value) {
    //logger.info('Volume update', { volume: roundedVolume, requestId: analyserSetupRequestId })
    lastLoggedVolume.value = roundedVolume
  }

  let shouldUpdateVolume = true

  if (props.deviceKind === 'audiooutput') {
    const now = Date.now()
    if (outputPlaybackActive.value && playbackStartedAt.value === null) {
      playbackStartedAt.value = now
    }

    const startGrace =
      playbackStartedAt.value !== null && now - playbackStartedAt.value <= ZERO_EDGE_GRACE_MS
    const endGrace =
      playbackEndedAt.value !== null && now - playbackEndedAt.value <= ZERO_EDGE_GRACE_MS
    const isZeroVolume = data.volume === 0
    const allowZero = !outputPlaybackActive.value || startGrace || endGrace

    if (isZeroVolume && !allowZero) {
      shouldUpdateVolume = false
    } else if (isZeroVolume) {
      if (!isOutputCurrentlySilent.value) {
        isOutputCurrentlySilent.value = true
        emit('output-silence')
      }
    } else if (isOutputCurrentlySilent.value) {
      isOutputCurrentlySilent.value = false
      emit('output-sound')
    }
  }

  if (shouldUpdateVolume) {
    volume.value = data.volume
  }

  if (data.volume > 0) {
    soundPicked()
  }

  if (props.deviceKind !== 'audioinput') return

  if (data.volume > volumeThreshold) {
    clearAutoRefreshTimeout()
  } else {
    scheduleAutoRefresh()
  }

  // If the volume is above the threshold and we are not recording, start recording
  if (data.volume > volumeThreshold && !audioAnalyserStore.isRecording) {
    startRecording()
    return
  }

  // If we are recording and the recording time is above the limit, stop recording
  if (
    audioAnalyserStore.isRecording &&
    Date.now() - audioAnalyserStore.getRecordingStartTime >= recordingLengthLimit
  ) {
    clearManagedTimeout(playbackTimeoutId)
    stopRecording(onRecordingFinished)
    return
  }

  // If the volume is above the threshold and we are recording, set a timeout to stop recording
  if (data.volume > volumeThreshold && audioAnalyserStore.isRecording) {
    clearManagedTimeout(playbackTimeoutId)

    playbackTimeoutId.value = setManagedTimeout(() => {
      if (isUnmounting) return
      stopRecording(onRecordingFinished)
    }, playbackWaitTime)
  }
}

function onPlayMusicClick(): Promise<void> {
  return new Promise((resolve, reject) => {
    const ensureAnalyserReady = async () => {
      if (!audioAnalyserStore.hasAudioPlayerSetUp) {
        logger.info('Audio player missing when attempting to play, rebuilding analyser')
        try {
          await setupAudioAnalyser()
        } catch (error) {
          logger.error('Failed to rebuild analyser before playback', error)
          throw error
        }
      }

      if (!audioAnalyserStore.hasAudioPlayerSetUp) {
        throw new Error('No audio player available after rebuild attempt')
      }
    }

    ensureAnalyserReady()
      .then(() => {
        try {
          audioAnalyserStore.playSound()
          resolve()
        } catch (error) {
          reject(error)
        }
      })
      .catch((error) => {
        reject(error)
      })
  })
}

async function finishAnalyserSetup(context: AudioContext, requestId: number) {
  await audioAnalyserStore
    .setupAudioAnalyser(context, props.deviceKind, recordingStream.value, volumeChangeCallback)
    .catch((error) => {
      logger.error('Failed to setup audio analyser', error)
      throw error
    })

  if (requestId !== analyserSetupRequestId) {
    logger.info('Discarding analyser setup result due to newer request', {
      requestId,
      latestRequestId: analyserSetupRequestId
    })
    return
  }
}

async function setupAudioAnalyser(): Promise<void> {
  const requestId = ++analyserSetupRequestId
  logger.info('Starting analyser setup', { requestId, deviceKind: props.deviceKind })

  const player = await audioAnalyserStore.initiateTrackToAnalyse().catch((error) => {
    logger.error('Failed to initiate test track', error)
    throw error
  })

  if (isUnmounting || requestId !== analyserSetupRequestId) {
    logger.info('Discarding analyser setup because component is unmounting or stale', {
      requestId,
      latestRequestId: analyserSetupRequestId,
      isUnmounting
    })
    return
  }

  audioPlayer.value = player
  if (props.deviceKind === 'audiooutput') {
    isOutputCurrentlySilent.value = true
    playbackStartedAt.value = null
    playbackEndedAt.value = Date.now()
    volume.value = 0
  }

  if (!audioContext.value) {
    const stopWatch = watch(audioContext, (newVal) => {
      if (newVal) {
        logger.info('Audio context available, finishing analyser setup.')
        finishAnalyserSetup(newVal, requestId).catch((err) => {
          logger.error('Deferred finishAnalyserSetup failed', err)
        })
        stopWatch()
      }
    })
  } else {
    await finishAnalyserSetup(audioContext.value, requestId)
  }
}

function startRecording() {
  audioAnalyserStore
    .startRecordingSound()
    .then(() => {
      // Handle successful start of recording if needed
    })
    .catch((error) => {
      logger.error('Failed to start recording:', error)
    })
}

function stopRecording(callback?: Function) {
  logger.info('Stopping recording')
  audioAnalyserStore.stopRecordingSound(callback)
}

function stopPlayback() {
  logger.info('Stopping playback')

  playbackSource.value?.stop()
  playbackSource.value?.disconnect()
  playbackSource.value = null
  replayingActive.value = false
  if (props.deviceKind === 'audiooutput') {
    volume.value = 0
    isOutputCurrentlySilent.value = true
    playbackStartedAt.value = null
    playbackEndedAt.value = Date.now()
  }
}

function soundPicked() {
  emit('sound-picked')
  clearAutoRefreshTimeout()
}

async function startPlayback(audioBlob: Blob) {
  try {
    replayingActive.value = true
    const context = audioContext.value || new (window.AudioContext || window.webkitAudioContext)()

    const arrayBuffer = await audioBlob.arrayBuffer()
    const audioBuffer = await context.decodeAudioData(arrayBuffer)

    const source = context.createBufferSource()
    source.buffer = audioBuffer

    source.connect(context.destination)

    source.onended = function () {
      replayingActive.value = false
      playbackFinishedCallback.value()
    }

    source.start()
    playbackSource.value = source
  } catch (error) {
    logger.error('Error playing back sound')
    replayingActive.value = false
    playbackFinishedCallback.value()
  }
}

function onPlaybackFinished() {
  logger.info('Playback finished')
  if (!playbackSource.value) return
  audioAnalyserStore.setShowRecorderOutput(true)
  emit('playback-finished')
}

function onRecordingFinished({ inputBlob }: { inputBlob: Blob; inputDuration: number }) {
  logger.info('Recording finished', inputBlob)
  volume.value = 0
  startPlayback(inputBlob)
}

defineExpose({
  onPlayMusicClick,
  triggerAutoRefresh
})
</script>

<style lang="scss" scoped>
.mic-input {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--primary);
}
.mic-input-volume-container {
  width: 100%;
  height: 20px;
  border: 1px solid red;
}
.mic-input-volume-indicator {
  width: 0%;
  height: 20px;
  background-color: red;
}

.volumeIndicator {
  display: flex;
  align-items: center;
  width: 100%;

  .soundPreview {
    overflow: hidden;
    height: 35px;
    width: 100%;
    position: relative;

    img {
      position: absolute;
      bottom: 0;
      left: 0;
      z-index: 100;
    }

    .soundBar {
      width: 100%;
      /*min-width: 15px;*/
      height: 35px;
      background: rgba(185, 154, 238, 0.5);
      background-color: $primary;
      position: absolute;
      bottom: 0;
      left: 0;
    }

    .greyBackground {
      width: 100%;
      min-width: 15px;
      height: 35px;
      background: #eee;

      position: absolute;
      bottom: 0;
      left: 0;
    }

    .bars {
      position: absolute;
      bottom: 0;
      left: 0;
      display: flex;
      justify-content: space-evenly;
      width: 100%;
      height: 100%;
      z-index: 2;

      .bar {
        // box-sizing: content-box;s
        height: 100%;
        border: 1px solid $white;
        // border: 4px solid red;
        flex-grow: 1;
        position: relative;
        overflow: hidden;
      }
    }
  }
  .recordingIndicator {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0.25rem 1rem;
    margin-left: 0.25rem;
    align-items: center;
    gap: 0.5rem;
    color: $secondary;
    border: 2px solid darken($light, 10%);
    border-radius: 5px;
    font-weight: 600;
    min-width: 8rem;

    .button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;

      .start {
        color: $primary;
      }

      .stop {
        color: $danger;
      }
    }

    .indicator {
      color: $success;
    }

    &.recording {
      color: $secondary;

      .indicator {
        color: $danger;
        @include animation(flashing 1.25s infinite);

        @include keyframes(flashing) {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.25;
          }
          100% {
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
